import React from "react";

interface TalkableGenieAvatarProps {
  avatarSrc: string;
  onClick?: () => void;
  isConnecting?: boolean;
  speaking?: boolean;
  listening?: boolean;
  connected?: boolean;
  className?: string; // allow passing exact size classes from parent (to avoid visual changes)
}

const TalkableGenieAvatar: React.FC<TalkableGenieAvatarProps> = ({
  avatarSrc,
  onClick,
  isConnecting = false,
  speaking = false,
  listening = false,
  connected = false,
  className = "h-24 w-24 md:h-28 md:w-28",
}) => {
  const showUseMe = !(isConnecting || speaking || listening || connected);
  const isActive = connected || speaking || listening;
  return (
    <div className={`relative ${className}`}>
      {/* Animated ring halo - outside visual layers; middle image unchanged */}
      <div
        className={`absolute -inset-1 rounded-full pointer-events-none wcgd-ring ${
          isConnecting ? "wcgd-connecting" : speaking ? "wcgd-speaking" : listening ? "wcgd-listening" : ""
        }`}
      />

      {/* "Use me" hint - hidden when active */}
      <div
        className={`absolute top-1/2 -translate-y-1/2 left-[-34px] md:left-[-40px] pointer-events-none transition-opacity duration-200 ${
          showUseMe ? "opacity-100" : "opacity-0"
        }`}
        aria-hidden
      >
        <span className="wcgd-useme text-[10px] md:text-xs font-semibold text-[#1f2a44] px-2 py-1 rounded-full border bg-white shadow-sm">
          Use me
        </span>
      </div>

      {/* End-call hint - appears when active; does not block clicks */}
      <div
        className={`absolute -bottom-2 -right-2 md:-bottom-2 md:-right-2 pointer-events-none transition-opacity duration-150 ${
          isActive ? "opacity-100" : "opacity-0"
        }`}
        aria-hidden
      >
        <span className="wcgd-endcall text-[10px] font-semibold text-white px-2 py-1 rounded-full shadow">
          <span className="wcgd-stop mr-1" />End
        </span>
      </div>

      {/* Original avatar container - unchanged styling */}
      <div
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : -1}
        onClick={onClick}
        className="h-full w-full rounded-full shadow-sm overflow-hidden flex items-center justify-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(78,156,255,0.16) 0%, rgba(214,64,159,0.16) 100%)",
          outline: "none",
        }}
      >
        <img src={avatarSrc} alt="" className="h-[83%] w-[83%] md:h-[86%] md:w-[86%] object-contain" />
      </div>

      {/* Local CSS for subtle ring animations (no impact on center image) */}
      <style>{`
        .wcgd-ring {
          box-shadow: 0 0 0 0 rgba(78,156,255,0); 
          transition: box-shadow .2s ease; 
        }
        .wcgd-connecting {
          animation: wcgdSpin 1s linear infinite;
          border: 3px solid transparent;
          border-top-color: rgba(78,156,255,0.8);
          border-right-color: rgba(214,64,159,0.8);
        }
        .wcgd-speaking {
          box-shadow: 0 0 24px 8px rgba(78,156,255,.45), 0 0 60px 20px rgba(214,64,159,.20);
        }
        .wcgd-listening {
          animation: wcgdPulse 1.8s ease-in-out infinite;
          box-shadow: 0 0 16px 4px rgba(78,156,255,.25);
        }
        @keyframes wcgdSpin { to { transform: rotate(360deg); } }
        @keyframes wcgdPulse {
          0%,100% { box-shadow: 0 0 10px 2px rgba(78,156,255,.18); }
          50% { box-shadow: 0 0 22px 10px rgba(78,156,255,.35); }
        }
        .wcgd-useme {
          animation: wcgdNudge 1.8s ease-in-out infinite;
          background: linear-gradient(90deg, rgba(78,156,255,0.12) 0%, rgba(214,64,159,0.12) 100%);
          border-color: rgba(78,156,255,0.35);
        }
        @keyframes wcgdNudge {
          0%,100% { transform: translateX(0); }
          50% { transform: translateX(3px); }
        }
        .wcgd-endcall {
          background: linear-gradient(90deg, #4E9CFF, #D6409F);
        }
        .wcgd-stop {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 9999px;
          background: #ffffff;
          border: 2px solid #4E9CFF;
          vertical-align: -1px;
        }
      `}</style>
    </div>
  );
};

export default TalkableGenieAvatar;


